import { cleanDocument, validateCpfCnpj } from './cpfCnpjValidator';

/** Limite do Pagar.me: `default_bank_account.holder_name` ≤ 30 caracteres. */
export const PAGARME_BANK_HOLDER_NAME_MAX = 30;

export type RegisterType = 'individual' | 'corporation';

export type PagarmeRecipientFormInput = {
  registerType: RegisterType;
  parishDocDigits: string;
  parishCpfCnpjType?: 'cpf' | 'cnpj' | null;
  parishName: string;
  holderName: string;
  companyName: string;
  tradingName: string;
  birthdate: string;
  repBirthdate: string;
  repName: string;
  repEmail: string;
  repDocument: string;
  sameRepAsHolder: boolean;
  bankCode: string;
  branchNumber: string;
  accountNumber: string;
  accountCheckDigit: string;
};

export type PagarmeRecipientFormErrors = Partial<
  Record<
    | 'registerType'
    | 'holderName'
    | 'companyName'
    | 'bankCode'
    | 'branchNumber'
    | 'accountNumber'
    | 'accountCheckDigit'
    | 'birthdate'
    | 'repBirthdate'
    | 'repName'
    | 'repEmail'
    | 'repDocument'
    | 'parishDoc',
    string
  >
>;

const COMPANY_MARKERS =
  /\b(ltda|ltda\.|me\b|e\.?p\.?p\.?|eireli|s\/a|s\.a\.|assoc|associa[cç][aã]o|par[oó]quia|comunidade|fund[aã]o|igreja|n\.?p\.?o\.?|ong)\b/i;

const INSTITUTION_MARKERS =
  /\b(pastoral|pastorais|musica|música|liturgic|litúrgic|sacrament|paroquial|comunit|centro|nucleo|núcleo|servic|serviço|minister|movimento|grupo|projeto|crist|catolic|católic|amigos|amigo|jovens|familia|família|acao|ação|social|cultural|benefic|caridade|entretenimento|produ[cç]|eventos?|empresa|com[eé]rcio|ind[uú]stria|show|festival|studio|est[uú]dio|m[ií]dia|digital|tech)\b/i;

/** Nome que parece pessoa física (2–3 palavras, sem marcadores de empresa/instituição). */
export function looksLikePersonName(name: string): boolean {
  const t = name.trim();
  if (!t || t.length < 3) return false;
  if (COMPANY_MARKERS.test(t)) return false;
  if (INSTITUTION_MARKERS.test(t)) return false;
  if (/\d/.test(t)) return false;
  const parts = t.split(/\s+/).filter(Boolean);
  return parts.length >= 2 && parts.length <= 3;
}

export function inferRegisterTypeFromDocument(docDigits: string): RegisterType | null {
  if (docDigits.length === 11) return 'individual';
  if (docDigits.length === 14) return 'corporation';
  return null;
}

export function suggestCompanyHolderName(input: {
  companyName: string;
  tradingName: string;
  parishName: string;
}): string {
  return (input.tradingName || input.companyName || input.parishName || '').trim();
}

/** Razão social efetiva para conta PJ (ignora titular que pareça PF). Prefere titular explícito (≤30). */
export function resolveCorporateHolderName(input: {
  holderName: string;
  companyName: string;
  tradingName: string;
  parishName: string;
}): string {
  const candidates = [input.holderName, input.companyName, input.tradingName, input.parishName]
    .map((s) => s.trim())
    .filter(Boolean);
  const nonPerson = candidates.find((c) => !looksLikePersonName(c));
  const picked = nonPerson || candidates[0] || '';
  return picked.slice(0, PAGARME_BANK_HOLDER_NAME_MAX).trim();
}

function isValidDdMmYyyy(value: string): boolean {
  const raw = value.trim();
  if (!raw) return false;
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(raw.replace(/\D/g, '').length === 8
    ? `${raw.replace(/\D/g, '').slice(0, 2)}/${raw.replace(/\D/g, '').slice(2, 4)}/${raw.replace(/\D/g, '').slice(4, 8)}`
    : raw);
  if (!m) return false;
  const day = Number(m[1]);
  const month = Number(m[2]);
  const year = Number(m[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31 || year < 1900 || year > new Date().getFullYear()) {
    return false;
  }
  return true;
}

export function validatePagarmeRecipientForm(input: PagarmeRecipientFormInput): {
  errors: PagarmeRecipientFormErrors;
  blockingMessages: string[];
} {
  const errors: PagarmeRecipientFormErrors = {};
  const blockingMessages: string[] = [];

  const docValidation = validateCpfCnpj(input.parishDocDigits);
  if (!docValidation.isValid) {
    errors.parishDoc = 'CPF/CNPJ da paróquia inválido. Corrija em Configurações antes de sincronizar.';
    blockingMessages.push(errors.parishDoc);
  }

  const inferred = inferRegisterTypeFromDocument(input.parishDocDigits);
  if (inferred && input.registerType !== inferred) {
    errors.registerType =
      inferred === 'corporation'
        ? 'O cadastro da paróquia usa CNPJ — selecione Pessoa jurídica.'
        : 'O cadastro da paróquia usa CPF — selecione Pessoa física.';
    blockingMessages.push(errors.registerType);
  }

  const holderTrim =
    input.parishDocDigits.length === 14 && input.registerType === 'corporation'
      ? resolveCorporateHolderName(input)
      : input.holderName.trim();
  if (!holderTrim) {
    errors.holderName =
      input.registerType === 'corporation'
        ? 'Informe a razão social da empresa (igual ao cadastro bancário).'
        : 'Informe o nome do titular como está no banco.';
    blockingMessages.push(errors.holderName);
  } else if (holderTrim.length > PAGARME_BANK_HOLDER_NAME_MAX) {
    errors.holderName = `Titular da conta no Pagar.me: no máximo ${PAGARME_BANK_HOLDER_NAME_MAX} caracteres (hoje: ${holderTrim.length}).`;
    blockingMessages.push(errors.holderName);
  } else if (input.parishDocDigits.length === 14 && looksLikePersonName(holderTrim)) {
    errors.holderName =
      'Com CNPJ, use a razão social da empresa (igual ao cadastro bancário), não o nome de uma pessoa.';
    blockingMessages.push(errors.holderName);
  }

  const bank = input.bankCode.replace(/\D/g, '');
  if (!bank || bank.length < 3) {
    errors.bankCode = 'Informe o código do banco com 3 dígitos (ex.: 341 Itaú, 077 Inter).';
    blockingMessages.push(errors.bankCode);
  }

  if (!input.branchNumber.replace(/\D/g, '').trim()) {
    errors.branchNumber = 'Agência é obrigatória.';
    blockingMessages.push(errors.branchNumber);
  }

  if (!input.accountNumber.replace(/\D/g, '').trim()) {
    errors.accountNumber = 'Número da conta é obrigatório.';
    blockingMessages.push(errors.accountNumber);
  }

  if (String(input.accountCheckDigit).trim() === '') {
    errors.accountCheckDigit = 'Informe o dígito da conta (pode ser 0 ou X).';
    blockingMessages.push(errors.accountCheckDigit);
  }

  if (input.registerType === 'individual') {
    if (!isValidDdMmYyyy(input.birthdate)) {
      errors.birthdate = 'Data de nascimento do titular no formato DD/MM/AAAA.';
      blockingMessages.push(errors.birthdate);
    }
  } else {
    const companyTrim = input.companyName.trim();
    if (!companyTrim) {
      errors.companyName = 'Razão social é obrigatória para Pessoa Jurídica (CNPJ).';
      blockingMessages.push(errors.companyName);
    } else if (looksLikePersonName(companyTrim)) {
      errors.companyName = 'Razão social não pode ser nome de pessoa física — use o nome da empresa como no banco.';
      blockingMessages.push(errors.companyName);
    }

    if (!isValidDdMmYyyy(input.repBirthdate)) {
      errors.repBirthdate = 'Data de nascimento do representante no formato DD/MM/AAAA.';
      blockingMessages.push(errors.repBirthdate);
    }
    if (!input.repName.trim()) {
      errors.repName = 'Nome do representante legal é obrigatório.';
      blockingMessages.push(errors.repName);
    }
    if (!input.repEmail.trim()) {
      errors.repEmail = 'E-mail do representante legal é obrigatório.';
      blockingMessages.push(errors.repEmail);
    }
    const repDoc = cleanDocument(input.repDocument);
    if (!validateCpfCnpj(repDoc).isValid || repDoc.length !== 11) {
      errors.repDocument = 'CPF do representante legal é obrigatório e deve ser válido.';
      blockingMessages.push(errors.repDocument);
    }
  }

  return { errors, blockingMessages };
}
